/**
 * Push notification deep link coordination.
 * localStorage flag bridges push tap → MainLayout module/chat opening.
 *
 * Supported deep link types:
 *   xavior://inbox        → open first gmail module
 *   xavior://calendar     → open first event_list module
 *   xavior://tasks        → open first task_list module
 *   xavior://conversation/{id} → open specific conversation
 */

const DEEP_LINK_KEY = 'xavior:pendingDeepLink';
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours — user may tap notification hours later

export type DeepLinkType = 'module' | 'conversation';

export interface PendingDeepLink {
  type: DeepLinkType;
  /** Module type to open (e.g. 'email', 'event_list', 'task_list') */
  moduleType?: string;
  /** Specific conversation ID to navigate to */
  conversationId?: string;
}

/** Map deep link paths to module types. */
const DEEP_LINK_MODULE_MAP: Record<string, string> = {
  inbox: 'email',
  calendar: 'event_list',
  tasks: 'task_list',
  notes: 'docs',
  docs: 'docs',
  contacts: 'contact_list',
  files: 'drive',
  drive: 'drive',
  photos: 'photos',
  places: 'place_list',
  stats: 'stat_list',
};

/**
 * Parse a xavior:// deep link and store it for MainLayout to consume.
 * Returns true if the deep link was recognized and stored.
 */
export function setPendingDeepLink(deepLink: string): boolean {
  const path = deepLink.slice('xavior://'.length);

  // Module deep links: xavior://inbox, xavior://calendar, etc.
  const moduleType = DEEP_LINK_MODULE_MAP[path];
  if (moduleType) {
    localStorage.setItem(DEEP_LINK_KEY, JSON.stringify({
      type: 'module',
      moduleType,
      ts: Date.now(),
    }));
    return true;
  }

  // Conversation deep link: xavior://conversation/{id}
  if (path.startsWith('conversation/')) {
    const conversationId = path.slice('conversation/'.length);
    if (conversationId) {
      localStorage.setItem(DEEP_LINK_KEY, JSON.stringify({
        type: 'conversation',
        conversationId,
        ts: Date.now(),
      }));
      return true;
    }
  }

  return false;
}

export function consumePendingDeepLink(): PendingDeepLink | null {
  const raw = localStorage.getItem(DEEP_LINK_KEY);
  if (!raw) return null;
  localStorage.removeItem(DEEP_LINK_KEY);

  try {
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > EXPIRY_MS) return null;
    return { type: parsed.type, moduleType: parsed.moduleType, conversationId: parsed.conversationId };
  } catch {
    return null;
  }
}
